import { useContext, useEffect } from "react";
import { GlobalStateContext } from "../layouts/GlobalStates";
import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../feedback/Layout";
import FoodSection from "../feedback/Food";
import Landing from "../feedback/Landing";
import ShareProduct from "../feedback/ShareProduct";
import Contents from "../feedback/Contents";
import More from "../feedback/More";
import Similar from "../feedback/Similar";
import DidbanShare from "../feedback/DidbanShare";
import Rating from "../feedback/Rating";
import Product from "../feedback/Product";
import NotFound from "../feedback/404";
import NoInternet from "../feedback/500";
import { getFeedbackRouteId } from "../feedback/helperFunctions";
import { getProduct } from "../feedback/universalData";
import { getAjax } from "../feedback/axiosMethods";

function FeedbackRoutes() {
    const { feedbackAppGlobalState } = useContext(GlobalStateContext);
    const [state, setState] = feedbackAppGlobalState;
    const location = useLocation();
    const routeId = getFeedbackRouteId(location.pathname);
    const { i18n } = useTranslation();

    useEffect(() => {
        if (!routeId) return;

        const current = feedbackAppGlobalState[0];
        if (
            current.productId === routeId &&
            current.data?.form &&
            current.ajax?.item
        ) {
            return;
        }

        let cancelled = false;
        // React Router preserves percent-encoded URL path segments. Decode first
        // so a QR code ending in `%3D` is sent as `=` once, not as `%253D`.
        let scannedCode;
        try {
            scannedCode = encodeURIComponent(decodeURIComponent(routeId));
        } catch {
            // Let the API report an invalid, malformed URL segment.
            scannedCode = encodeURIComponent(routeId);
        }

        // Item information fields are configured in the organisation's default
        // language. Do not filter the public feedback form by browser locale:
        // headers such as `fa-IR` do not match the stored `fa` value and the
        // API then returns an empty feature list.
        getAjax(getProduct.replace(":id", scannedCode)).then(
            (response) => {
                if (!response || cancelled) return;

                const criteria_replies = response.form.criteria_list.map(
                    (criteria) => ({
                        criteria: criteria.id,
                        state: undefined,
                        description: "",
                    })
                );

                setState((prev) => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        form: response.form.id,
                        item: response.item.id,
                        company: response.item.company,
                        criteria_replies,
                    },
                    ajax: response,
                    productId: routeId,
                }));
            }
        );

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeId, i18n.language, setState]);

    const formReady =
        Boolean(state.data?.form) &&
        Boolean(state.ajax?.item) &&
        state.productId === routeId;

    // These paths must stay relative. This <Routes> is a descendant of the
    // `/feedback/*` route in AppRoutes, so it matches against the pathname left
    // over after `/feedback`. An absolute `/feedback/:id` here matches nothing
    // and the page silently renders empty inside the Layout chrome.
    const routes = (
        <>
            <Route path="menu/:id" element={<FoodSection />} />
            <Route path="share-product/:id" element={<ShareProduct />} />
            <Route path="contents/:id" element={<Contents />} />
            <Route path="more/:id" element={<More />} />
            <Route path="similar/:id" element={<Similar />} />
            <Route path="didban-share/:id" element={<DidbanShare />} />
            <Route path="form/:id" element={<Rating />} />
            <Route path=":id/share/:isIntro" element={<Product />} />
            <Route path=":id/:companyId" element={<Product />} />
            <Route path=":id" element={<Product />} />
            <Route path="400" element={<NotFound />} />
            <Route path="500" element={<NoInternet />} />
        </>
    );

    if (routeId && !formReady) {
        return <Landing />;
    }

    if (formReady) {
        return (
            <Layout id={routeId}>
                <Routes>{routes}</Routes>
            </Layout>
        );
    }

    return <Routes>{routes}</Routes>;
}

export default FeedbackRoutes;
